import { DownloadOutlined, InboxOutlined, PrinterOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Flex, Table, Tag, Typography, Upload } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import {
  checkStaffImport,
  importStaff,
  type CreatedStaff,
  type StaffImportCheck,
  type StaffRowCheck,
} from './api';
import { STAFF_TEMPLATE, downloadText, printSlips, slipsCsv, slipsHtml } from './staff-slips';

const { Text } = Typography;

const STATUS_COLOR: Record<StaffRowCheck['status'], string> = {
  CREATE: 'success',
  EXISTS: 'default',
  INVALID: 'error',
};

/**
 * Staff accounts from NKS's staff spreadsheet (spec 2026-10-27).
 *
 * Choosing a file checks it at once and writes nothing; creating is a separate,
 * deliberate press, refused while any row can't be imported. The temporary
 * passwords come back once and live only in this drawer, so closing it before
 * printing or downloading them asks first.
 */
export function StaffImportDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<StaffImportCheck | null>(null);
  const [created, setCreated] = useState<CreatedStaff[] | null>(null);
  const [saved, setSaved] = useState(false);

  // Leaving the page loses the passwords as surely as closing the drawer.
  const unsaved = created !== null && created.length > 0 && !saved;
  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [unsaved]);

  const reset = (): void => {
    setCsv(null);
    setFileName('');
    setError(null);
    setChecked(null);
    setCreated(null);
    setSaved(false);
  };

  const fail = (e: unknown): void => {
    setError(e instanceof ApiError ? e.message : t('iam.genericError'));
  };

  const check = useMutation({
    mutationFn: checkStaffImport,
    onSuccess: (result) => {
      setChecked(result);
    },
    onError: fail,
  });

  const create = useMutation({
    mutationFn: importStaff,
    onSuccess: (result) => {
      setCreated(result.created);
      onCreated();
    },
    onError: fail,
  });

  const close = (): void => {
    if (unsaved) {
      modal.confirm({
        title: t('iam.import.closeTitle'),
        content: t('iam.import.closeBody'),
        okText: t('iam.import.closeAnyway'),
        okButtonProps: { danger: true },
        cancelText: t('iam.import.keepOpen'),
        onOk: () => {
          reset();
          onClose();
        },
      });
      return;
    }
    reset();
    onClose();
  };

  const words = {
    heading: t('iam.import.slipHeading'),
    address: t('iam.import.slipAddress'),
    username: t('iam.username'),
    password: t('iam.import.temporaryPassword'),
    roles: t('iam.roles'),
    firstSignIn: t('iam.import.slipFirstSignIn'),
  };

  return (
    <Drawer
      className="nks-action-drawer"
      width={760}
      open={open}
      title={t('iam.import.title')}
      onClose={close}
      extra={
        created === null && checked !== null && csv !== null ? (
          <Button
            type="primary"
            loading={create.isPending}
            disabled={checked.invalid > 0 || checked.toCreate === 0}
            onClick={() => {
              setError(null);
              create.mutate(csv);
            }}
          >
            {t('iam.import.create', { count: checked.toCreate })}
          </Button>
        ) : null
      }
    >
      <Flex vertical gap={16}>
        {error !== null && <Alert type="error" showIcon message={error} />}

        {created !== null ? (
          <>
            <Alert
              type="warning"
              showIcon
              message={t('iam.import.createdTitle', { count: created.length })}
              description={t('iam.import.createdBody')}
            />
            <Flex gap={8} wrap>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                disabled={created.length === 0}
                onClick={() => {
                  // Not "saved": the print dialog may be cancelled, so closing still asks.
                  printSlips(slipsHtml(created, words, window.location.origin));
                }}
              >
                {t('iam.import.printSlips')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                disabled={created.length === 0}
                onClick={() => {
                  downloadText('nks-sign-in-slips.csv', slipsCsv(created));
                  setSaved(true);
                }}
              >
                {t('iam.import.download')}
              </Button>
            </Flex>
            <Table<CreatedStaff>
              size="small"
              rowKey="uid"
              pagination={false}
              dataSource={created}
              scroll={{ x: 560 }}
              columns={[
                { title: t('iam.name'), dataIndex: 'name' },
                { title: t('iam.username'), dataIndex: 'username', render: (v: string) => <Text code>{v}</Text> },
                {
                  title: t('iam.import.temporaryPassword'),
                  dataIndex: 'temporaryPassword',
                  render: (v: string) => <Text code copyable>{v}</Text>,
                },
                { title: t('iam.roles'), render: (_: unknown, r: CreatedStaff) => r.roles.join(', ') },
              ]}
            />
          </>
        ) : (
          <>
            <Text type="secondary">{t('iam.import.intro')}</Text>
            <div>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  downloadText('nks-staff-template.csv', STAFF_TEMPLATE);
                }}
              >
                {t('iam.import.template')}
              </Button>
            </div>
            <Upload.Dragger
              accept=".csv,text/csv"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                void file.text().then((text) => {
                  setCsv(text);
                  setFileName(file.name);
                  setChecked(null);
                  setError(null);
                  check.mutate(text);
                });
                return false;
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {fileName === '' ? t('iam.import.choose') : t('iam.import.chosen', { name: fileName })}
              </p>
              <p className="ant-upload-hint">{t('iam.import.chooseHint')}</p>
            </Upload.Dragger>

            {checked !== null && (
              <>
                <Flex gap={8} wrap>
                  <Tag color="success">{t('iam.import.willCreate', { count: checked.toCreate })}</Tag>
                  <Tag>{t('iam.import.alreadyHave', { count: checked.existing })}</Tag>
                  {checked.invalid > 0 && (
                    <Tag color="error">{t('iam.import.cannotImport', { count: checked.invalid })}</Tag>
                  )}
                </Flex>
                {checked.invalid > 0 && <Alert type="error" showIcon message={t('iam.import.fixFirst')} />}
                <Table<StaffRowCheck>
                  size="small"
                  rowKey="line"
                  loading={check.isPending}
                  pagination={false}
                  dataSource={checked.rows}
                  scroll={{ x: 640 }}
                  columns={[
                    { title: t('iam.import.row'), dataIndex: 'line', width: 56 },
                    {
                      title: t('iam.name'),
                      render: (_: unknown, r: StaffRowCheck) => (
                        <Flex vertical>
                          <Text>{r.name}</Text>
                          <Text type="secondary" className="tabular">
                            {r.employeeNo} · {r.username}
                          </Text>
                        </Flex>
                      ),
                    },
                    {
                      title: t('iam.roles'),
                      render: (_: unknown, r: StaffRowCheck) => (
                        <Flex vertical>
                          <Text>{r.roles.join(', ')}</Text>
                          <Text type="secondary">{r.branches.join(', ')}</Text>
                        </Flex>
                      ),
                    },
                    {
                      title: t('iam.status'),
                      width: 220,
                      render: (_: unknown, r: StaffRowCheck) => (
                        <Flex vertical gap={2}>
                          <span>
                            <Tag color={STATUS_COLOR[r.status]}>{t(`iam.import.status.${r.status}`)}</Tag>
                          </span>
                          {r.problems.map((p) => (
                            <Text key={p} type="danger">
                              {p}
                            </Text>
                          ))}
                          {r.notes.map((n) => (
                            <Text key={n} type="warning">
                              {n}
                            </Text>
                          ))}
                        </Flex>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Flex>
    </Drawer>
  );
}
